import {Theme, alpha} from '@mui/material/styles';

const createCssVariables = (theme: Theme) => {
  const isDark = theme.palette.mode === 'dark';

  return {
    ':root': {
      '--shadow-sm': isDark
        ? '0 1px 2px rgba(0, 0, 0, 0.3)'
        : '0 1px 2px rgba(0, 0, 0, 0.05)',
      '--shadow-md': isDark
        ? '0 1px 3px rgba(0, 0, 0, 0.4)'
        : '0 1px 3px rgba(0, 0, 0, 0.1)',
      '--shadow-lg': isDark
        ? '0 8px 24px rgba(0, 0, 0, 0.4)'
        : '0 8px 24px rgba(0, 0, 0, 0.12)',
      '--shadow-popover': isDark
        ? '0 4px 12px rgba(0, 0, 0, 0.5)'
        : '0 4px 12px rgba(0, 0, 0, 0.15)',
      '--shadow-focus-sm': isDark
        ? `0 0 0 3px ${alpha(theme.palette.grey[600], 0.19)}`
        : `0 0 0 3px ${alpha(theme.palette.grey[400], 0.13)}`,

      '--interactive-hover': isDark
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)',
      '--interactive-active': isDark
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.03)',
      '--interactive-selected': alpha(
        theme.palette.primary.main,
        isDark ? 0.15 : 0.05,
      ),
      '--interactive-selected-hover': alpha(
        theme.palette.primary.main,
        isDark ? 0.18 : 0.08,
      ),
      '--interactive-highlighted': isDark
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)',

      '--badge-default-bg': isDark
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.06)',
      '--badge-primary-bg': alpha(
        theme.palette.primary.main,
        isDark ? 0.2 : 0.1,
      ),
      '--badge-success-bg': alpha(
        theme.palette.success.main,
        isDark ? 0.2 : 0.1,
      ),
      '--badge-warning-bg': alpha(
        theme.palette.warning.main,
        isDark ? 0.2 : 0.1,
      ),
      '--badge-error-bg': alpha(theme.palette.error.main, isDark ? 0.2 : 0.1),
      '--badge-info-bg': alpha(theme.palette.info.main, isDark ? 0.2 : 0.1),

      '--surface-text': theme.palette.text.primary,
      '--surface-text-muted': theme.palette.text.disabled,
      '--surface-text-secondary': theme.palette.text.secondary,
      '--surface-bg': theme.palette.background.default,
      '--surface-bg-hover': isDark
        ? theme.palette.action.hover
        : theme.palette.grey[50],
      '--surface-bg-alt': theme.palette.grey[100],
      '--surface-border': theme.palette.divider,
      '--surface-border-hover': isDark
        ? theme.palette.grey[600]
        : theme.palette.grey[400],
      '--surface-border-dashed': isDark
        ? theme.palette.grey[300]
        : theme.palette.grey[400],

      '--gradient-block-suggestion': isDark
        ? 'linear-gradient(90deg, rgba(255, 100, 240, 0.15) 0%, rgba(100, 180, 255, 0.15) 100%)'
        : 'linear-gradient(90deg, #FFECFE 0%, #B5DEF8 100%)',
      '--gradient-home-suggestion': isDark
        ? 'linear-gradient(90deg, rgba(255, 180, 80, 0.15) 0%, rgba(255, 160, 30, 0.2) 100%)'
        : 'linear-gradient(90deg, #FFF3E0 0%, #FFCC80 100%)',

      '--insight-warning-bg': isDark
        ? `linear-gradient(180deg, ${alpha(theme.palette.warning.main, 0.12)} 0%, ${alpha(theme.palette.warning.main, 0.08)} 100%)`
        : 'linear-gradient(180deg, #FFF8EE 0%, #FFF1D9 100%)',
      '--insight-warning-border': isDark
        ? alpha(theme.palette.warning.main, 0.3)
        : '#F5E3C7',
      '--insight-error-bg': isDark
        ? `linear-gradient(180deg, ${alpha(theme.palette.error.main, 0.12)} 0%, ${alpha(theme.palette.error.main, 0.08)} 100%)`
        : 'linear-gradient(180deg, #FFF5F5 0%, #FCE8E8 100%)',
      '--insight-error-border': isDark
        ? alpha(theme.palette.error.main, 0.3)
        : '#F2D5D5',
      '--insight-info-bg': isDark
        ? `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.12)} 0%, ${alpha(theme.palette.info.main, 0.08)} 100%)`
        : 'linear-gradient(180deg, #F3F8FC 0%, #EEF4FA 100%)',
      '--insight-info-border': isDark
        ? alpha(theme.palette.info.main, 0.3)
        : '#CFE2F3',
      '--insight-note-bg': isDark
        ? `linear-gradient(180deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`
        : 'linear-gradient(180deg, #FBF9F7 0%, #F5F0EB 100%)',
      '--insight-note-border': isDark
        ? alpha(theme.palette.warning.main, 0.2)
        : '#E8DFD3',
    },
  };
};

export const CssBaselineOverride = (theme: Theme) => ({
  MuiCssBaseline: {
    styleOverrides: createCssVariables(theme),
  },
});

export {createCssVariables as cssVariables};
